import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useToast } from '../../hooks/useToast';
import api from '../../services/api';
import styles from './AuthPages.module.css';
import navStyles from '../../components/layouts/PublicLayout.module.css';

const ResetPasswordPage = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState({});
  const [accessToken, setAccessToken] = useState('');
  const [scrolled, setScrolled] = useState(false);

  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Extract access token from URL (Supabase puts it in hash fragment or query params)
  useEffect(() => {
    // Check hash fragment first (Supabase default)
    const hash = window.location.hash;
    if (hash) {
      const params = new URLSearchParams(hash.substring(1));
      const token = params.get('access_token');
      if (token) {
        setAccessToken(token);
        return;
      }
    }

    // Check query params as fallback
    const token = searchParams.get('access_token') || searchParams.get('token');
    if (token) {
      setAccessToken(token);
    }
  }, [searchParams]);

  const validatePassword = (pwd) => {
    if (pwd.length < 8) {
      return 'Password must be at least 8 characters';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});

    // Validation
    const newErrors = {};

    if (!password) {
      newErrors.password = 'Password is required';
    } else {
      const pwdError = validatePassword(password);
      if (pwdError) newErrors.password = pwdError;
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (!accessToken) {
      newErrors.general = 'Invalid or expired reset link. Please request a new one.';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    try {
      await api.post('/auth/reset-password', {
        accessToken,
        password,
        confirmPassword
      });
      setSuccess(true);
      toast.success('Password reset successfully!');
    } catch (error) {
      const message = error.message || 'Failed to reset password. The link may have expired.';
      setErrors({ general: message });
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // Show error if no token found
  if (!accessToken && !success) {
    return (
      <div className={styles.loginPage}>
        {/* Floating Navbar - same as login page */}
        <nav className={`${navStyles.navbar} ${scrolled ? navStyles.navbarScrolled : ''}`}>
          <Link to="/" className={navStyles.logo}>
            <img loading="lazy" src="/oval-logo.webp" alt="OVAL" className={navStyles.logoImg} />
          </Link>
          <div className={navStyles.navActions}>
            <Link to="/login" className={navStyles.signInBtn}>Sign In</Link>
            <Link to="/register" className={navStyles.signUpBtn}>Sign Up</Link>
          </div>
        </nav>

        <div className={styles.heroSide}>
          <img
            src="/images/log-in-page/login-hero.webp"
            alt="Oval Care"
            className={styles.heroImage}
          />
          <div className={styles.heroOverlay}>
            <div className={styles.heroContent}>
              <h1 className={styles.heroTitle}>
                Care<br />
                that's Clear,<br />
                Not Loud
              </h1>
            </div>
          </div>
        </div>

        <div className={styles.formSide}>
          <div className={styles.formContainer}>
            <div className={styles.formCard}>
              <div className={styles.errorMessage}>
                <div className={styles.errorIcon}>
                  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#EF4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="15" y1="9" x2="9" y2="15"></line>
                    <line x1="9" y1="9" x2="15" y2="15"></line>
                  </svg>
                </div>
                <h2 className={styles.formTitle}>Invalid Reset Link</h2>
                <p className={styles.formSubtitle}>
                  This password reset link is invalid or has expired. Please request a new one.
                </p>
                <div className={styles.successActions}>
                  <Link to="/forgot-password" className={styles.loginButton}>
                    Request New Link
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.loginPage}>
      {/* Floating Navbar - same as login page */}
      <nav className={`${navStyles.navbar} ${scrolled ? navStyles.navbarScrolled : ''}`}>
        <Link to="/" className={navStyles.logo}>
          <img loading="lazy" src="/oval-logo.webp" alt="OVAL" className={navStyles.logoImg} />
        </Link>
        <div className={navStyles.navActions}>
          <Link to="/login" className={navStyles.signInBtn}>Sign In</Link>
          <Link to="/register" className={navStyles.signUpBtn}>Sign Up</Link>
        </div>
      </nav>

      {/* Left Side - Hero Image */}
      <div className={styles.heroSide}>
        <img
          src="/images/log-in-page/login-hero.webp"
          alt="Oval Care"
          className={styles.heroImage}
        />
        <div className={styles.heroOverlay}>
          <div className={styles.heroContent}>
            <h1 className={styles.heroTitle}>
              Care<br />
              that's Clear,<br />
              Not Loud
            </h1>
            <p className={styles.heroSubtitle}>
              Discover Oval's modern approach to wellness, where frosted clarity meets quiet confidence.
            </p>
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className={styles.formSide}>
        <div className={styles.formContainer}>
          <div className={styles.formCard}>
            {!success ? (
              <>
                <div className={styles.formHeader}>
                  <h2 className={styles.formTitle}>Reset Your Password</h2>
                  <p className={styles.formSubtitle}>
                    Enter your new password below. Make sure it's at least 8 characters.
                  </p>
                </div>

                <form onSubmit={handleSubmit} className={styles.form}>
                  {errors.general && (
                    <div className={styles.errorBanner}>{errors.general}</div>
                  )}

                  <div className={styles.inputGroup}>
                    <div className={styles.inputWrapper}>
                      <span className={styles.inputIcon}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                          <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                        </svg>
                      </span>
                      <input
                        type="password"
                        placeholder="Enter new password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className={`${styles.input} ${errors.password ? styles.inputError : ''}`}
                        autoComplete="new-password"
                        autoFocus
                      />
                    </div>
                    {errors.password && <span className={styles.fieldError}>{errors.password}</span>}
                  </div>

                  <div className={styles.inputGroup}>
                    <div className={styles.inputWrapper}>
                      <span className={styles.inputIcon}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                          <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                        </svg>
                      </span>
                      <input
                        type="password"
                        placeholder="Confirm new password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className={`${styles.input} ${errors.confirmPassword ? styles.inputError : ''}`}
                        autoComplete="new-password"
                      />
                    </div>
                    {errors.confirmPassword && <span className={styles.fieldError}>{errors.confirmPassword}</span>}
                  </div>

                  <button
                    type="submit"
                    className={styles.loginButton}
                    disabled={loading}
                  >
                    {loading ? 'Resetting...' : 'Reset Password'}
                  </button>
                </form>

                <div className={styles.formFooterLink}>
                  <span>Remember your password? </span>
                  <Link to="/login" className={styles.supportLink}>Back to Login</Link>
                </div>
              </>
            ) : (
              <div className={styles.successMessage}>
                <div className={styles.successIcon}>
                  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                    <polyline points="22 4 12 14.01 9 11.01"></polyline>
                  </svg>
                </div>
                <h2 className={styles.formTitle}>Password Reset Complete!</h2>
                <p className={styles.formSubtitle}>
                  Your password has been successfully reset. You can now log in with your new password.
                </p>
                <div className={styles.successActions}>
                  <Link to="/login" className={styles.loginButton}>
                    Go to Login
                  </Link>
                </div>
              </div>
            )}
          </div>

          <div className={styles.pageFooter}>
            <span>© 2026 Oval Care Inc. All rights reserved.</span>
            <span className={styles.poweredBy}>
              Powered by <span className={styles.ovalLink}>Oval Care Inc.</span>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
